import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import {
  FIELD_AFFIX_CLASS,
  fieldControlClass,
  fieldFrameClass,
  type FieldLookProps,
} from "../../lib/field-frame.js";
import { TONES } from "../../lib/tones.js";
import { announce } from "../../primitives/announce.js";

/* ---------------- Input ----------------
   forwardRef targets the inner <input> (the focusable form element), not the
   layout wrapper, so consumers and FormField can focus/measure the control.
   states-check-ignore: the focus treatment lives in the field-frame recipe
   (lib/field-frame.ts) — the text-field look on the frame: focus-frame, or
   focus-field on the ghost inline frame.

   `countLimit` is a SOFT cap: a "n / limit" counter under the field, no
   native `maxLength`, so pasting past it keeps every character. Crossing it
   turns the counter the watch tone (`lib/tones.ts`) and announces "N over
   the limit" through the shared `announce()` region once, on the change
   that crosses — never again while it stays over. See Textarea for the same
   contract. */

interface InputOwnProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "size"
> {
  label?: string;
  hint?: string;
  error?: string;
  countLimit?: number;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

/** `variant` / `size` are the field look (lib/field-frame.ts). The native
 *  numeric `size` attribute is omitted; width is layout, set by the parent.
 *  `variant="readonly"` also sets native `readOnly`; `readOnly` alone keeps
 *  the default look. */
export type InputProps = InputOwnProps & FieldLookProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    countLimit,
    prefix,
    suffix,
    variant,
    size,
    className,
    id,
    disabled,
    readOnly,
    value,
    defaultValue,
    onChange,
    "aria-invalid": ariaInvalid,
    "aria-describedby": ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = React.useId();
  const inputId = id ?? autoId;
  // Empty-string error means "no error" — don't flag the field invalid.
  const hasError = !!error;
  const invalid = hasError || ariaInvalid === true || ariaInvalid === "true";
  const description = hasError || hint ? `${inputId}-description` : undefined;
  const describedBy =
    [ariaDescribedBy, description].filter(Boolean).join(" ") || undefined;

  const isControlled = value !== undefined;
  const [internalLen, setInternalLen] = React.useState(
    typeof defaultValue === "string" ? defaultValue.length : 0,
  );
  const len = isControlled
    ? typeof value === "string"
      ? value.length
      : 0
    : internalLen;
  const hasCountLimit = countLimit !== undefined;
  const overLimit = hasCountLimit && len > countLimit;
  const wasOverLimitRef = React.useRef(overLimit);
  React.useEffect(() => {
    if (overLimit && !wasOverLimitRef.current) {
      announce(`${len - countLimit!} over the limit`, { politeness: "polite" });
    }
    wasOverLimitRef.current = overLimit;
  }, [overLimit, len, countLimit]);

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-medium text-pb-text-secondary mb-1.5"
        >
          {label}
        </label>
      )}
      <div className={fieldFrameClass({ variant, size, invalid, disabled })}>
        {prefix && <span className={FIELD_AFFIX_CLASS}>{prefix}</span>}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          readOnly={variant === "readonly" || readOnly}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => {
            // Only a counted field tracks its length: every other uncontrolled
            // Input keeps typing free of re-renders, as before countLimit.
            if (!isControlled && hasCountLimit)
              setInternalLen(e.target.value.length);
            onChange?.(e);
          }}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          className={cn(fieldControlClass({ variant, size }), className)}
          {...rest}
        />
        {suffix && <span className={FIELD_AFFIX_CLASS}>{suffix}</span>}
      </div>
      {(hint || hasError) && (
        <p
          id={description}
          className={cn(
            "text-dense-1 mt-1",
            hasError ? "text-pb-red-text" : "text-pb-text-muted",
          )}
        >
          {hasError ? error : hint}
        </p>
      )}
      {hasCountLimit && (
        <p
          className={
            overLimit
              ? cn("text-dense-1 mt-1 text-end pb-tabular", TONES.watch.text)
              : "text-dense-1 mt-1 text-end pb-tabular text-pb-text-muted"
          }
        >
          {len} / {countLimit}
        </p>
      )}
    </div>
  );
});
